// Translation registry — keyed by language code, then by message key.
// Language codes follow ISO 639-1. We start with the 7 most-spoken Indian
// languages plus English. Extend by adding a new language object below;
// missing keys fall back to English at lookup time.
//
// Scope of strings here: action buttons, cricket vocabulary (extras, wicket
// types), common UI verbs (Save, Cancel, Confirm, etc.), and AI-chat starter
// prompts. We deliberately don't translate score numbers, player names, or
// technical labels (e.g. "MOFF", "WOFF" stump-line codes) because those are
// universal in cricket scorecards and translating them creates ambiguity.

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English",   native: "English" },
  { code: "hi", label: "Hindi",     native: "हिन्दी" },
  { code: "ta", label: "Tamil",     native: "தமிழ்" },
  { code: "te", label: "Telugu",    native: "తెలుగు" },
  { code: "kn", label: "Kannada",   native: "ಕನ್ನಡ" },
  { code: "mr", label: "Marathi",   native: "मराठी" },
  { code: "bn", label: "Bengali",   native: "বাংলা" },
  { code: "gu", label: "Gujarati",  native: "ગુજરાતી" },
];

// Message catalogue. Keep keys in dot.path form so we can group by area later.
const en = {
  "common.save":          "Save",
  "common.cancel":        "Cancel",
  "common.confirm":       "Confirm",
  "common.back":          "Back",
  "common.delete":        "Delete",
  "common.edit":          "Edit",
  "common.undo":          "Undo",
  "common.next":          "Next",
  "common.done":          "Done",
  "common.close":         "Close",
  "common.retry":         "Retry",
  "common.loading":       "Loading…",
  "common.language":      "Language",

  // Match list / nav
  "nav.dashboard":        "Dashboard",
  "nav.matches":          "Matches",
  "nav.players":          "Players",
  "nav.teams":             "Teams",
  "nav.competitions":     "Competitions",
  "nav.organisation":     "Team",
  "nav.subscription":     "Subscription",
  "nav.help":             "Help & guides",
  "nav.signout":          "Sign out",

  // BBB action buttons
  "bbb.save_ball":        "SAVE BALL",
  "bbb.undo_ball":        "UNDO BALL",
  "bbb.edit_ball":        "EDIT BALL",
  "bbb.scorecard":        "Scorecard",
  "bbb.more":             "MORE",

  // Extras row
  "extras.dead_ball":     "Dead ball",
  "extras.wide":          "Wide",
  "extras.no_ball":       "No ball",
  "extras.bye":           "Bye",
  "extras.leg_bye":       "Leg bye",
  "extras.short_run":     "Short run",
  "extras.quick_running": "Quick running",
  "extras.free_hit":      "Free hit",
  "extras.wicket":        "Wicket",
  "extras.drs":           "DRS",

  // Wicket types
  "wicket.bowled":        "Bowled",
  "wicket.lbw":           "LBW",
  "wicket.caught":        "Caught",
  "wicket.run_out":       "Run out",
  "wicket.stumped":       "Stumped",
  "wicket.hit_wicket":    "Hit wicket",
  "wicket.mankad":        "Mankad",
  "wicket.hit_twice":     "Hit twice",
  "wicket.obstructing":   "Obstructing",
  "wicket.retired":       "Retired",
  "wicket.timed_out":     "Timed out",

  // Spatial tabs
  "spatial.pitch_map":    "Pitch map",
  "spatial.height_impact":"Height impact",
  "spatial.wagon_wheel":  "Wagon wheel",

  // Header status pills
  "header.synced":        "Synced",
  "header.queued":        "Queued",
  "header.syncing":       "Syncing…",
  "header.offline":       "Offline",
  "header.live_link":     "Live link",
  "header.copied":        "Copied",
  "header.check_stats":   "Check stats",

  // Save errors / states
  "save.failed":          "Save failed",
  "save.just_now":        "just now",
  "save.seconds_ago":     "{n}s ago",
  "save.minutes_ago":     "{n}m ago",

  // Chat
  "chat.title":           "Scoring assistant",
  "chat.placeholder":     "Ask anything…",
  "chat.examples":        "Ask in plain English. Examples:",
  "chat.example_score":   '"what\'s the score"',
  "chat.example_strike":  '"who\'s on strike"',
  "chat.example_undo":    '"undo last ball"',
  "chat.example_edit":    '"change ball 4.3 to 4 runs"',
  "chat.thinking":        "thinking…",
  "chat.confirm_action":  "Confirm action",
};

// Translations below — best-effort cricket-scorer phrasing. Where a term
// (e.g. LBW, DRS) is universally used in English in Indian cricket, we keep
// it in English/Roman script even in the localised version.
const hi = {
  ...en,
  "common.save":          "सहेजें",
  "common.cancel":        "रद्द",
  "common.confirm":       "पुष्टि",
  "common.back":          "वापस",
  "common.delete":        "हटाएँ",
  "common.edit":          "बदलें",
  "common.undo":          "पूर्ववत",
  "common.next":          "अगला",
  "common.done":          "हो गया",
  "common.close":         "बंद",
  "common.retry":         "फिर से",
  "common.loading":       "लोड हो रहा…",
  "common.language":      "भाषा",
  "nav.dashboard":        "डैशबोर्ड",
  "nav.matches":          "मैच",
  "nav.players":          "खिलाड़ी",
  "nav.teams":            "टीमें",
  "nav.competitions":     "प्रतियोगिताएँ",
  "nav.organisation":     "संगठन",
  "nav.subscription":     "सदस्यता",
  "nav.help":             "सहायता",
  "nav.signout":          "साइन आउट",
  "bbb.save_ball":        "गेंद सहेजें",
  "bbb.undo_ball":        "गेंद वापस",
  "bbb.edit_ball":        "गेंद बदलें",
  "bbb.scorecard":        "स्कोरकार्ड",
  "bbb.more":             "और",
  "extras.dead_ball":     "मृत गेंद",
  "extras.wide":          "वाइड",
  "extras.no_ball":       "नो बॉल",
  "extras.bye":           "बाई",
  "extras.leg_bye":       "लेग बाई",
  "extras.short_run":     "शॉर्ट रन",
  "extras.quick_running": "तेज़ दौड़",
  "extras.free_hit":      "फ्री हिट",
  "extras.wicket":        "विकेट",
  "wicket.bowled":        "बोल्ड",
  "wicket.lbw":           "एलबीडब्ल्यू",
  "wicket.caught":        "कैच आउट",
  "wicket.run_out":       "रन आउट",
  "wicket.stumped":       "स्टम्प्ड",
  "wicket.hit_wicket":    "हिट विकेट",
  "wicket.mankad":        "मांकड़",
  "wicket.hit_twice":     "दो बार मारा",
  "wicket.obstructing":   "बाधा डालना",
  "wicket.retired":       "रिटायर",
  "wicket.timed_out":     "समय समाप्त",
  "spatial.pitch_map":    "पिच मैप",
  "spatial.height_impact":"ऊँचाई प्रभाव",
  "spatial.wagon_wheel":  "वैगन व्हील",
  "header.synced":        "सहेजा गया",
  "header.queued":        "क़तार",
  "header.syncing":       "सिंक हो रहा…",
  "header.offline":       "ऑफ़लाइन",
  "header.live_link":     "लाइव लिंक",
  "header.copied":        "कॉपी हुआ",
  "header.check_stats":   "आँकड़े जाँचें",
  "save.failed":          "सहेजना असफल",
  "save.just_now":        "अभी",
  "save.seconds_ago":     "{n} सेकंड पहले",
  "save.minutes_ago":     "{n} मिनट पहले",
  "chat.title":           "स्कोरिंग सहायक",
  "chat.placeholder":     "कुछ भी पूछें…",
  "chat.examples":        "सरल भाषा में पूछें। उदाहरण:",
  "chat.example_score":   '"स्कोर क्या है"',
  "chat.example_strike":  '"स्ट्राइक पर कौन है"',
  "chat.example_undo":    '"पिछली गेंद वापस"',
  "chat.example_edit":    '"गेंद 4.3 को 4 रन करें"',
  "chat.thinking":        "सोच रहा है…",
  "chat.confirm_action":  "क्रिया की पुष्टि करें",
};

const ta = {
  ...en,
  "common.save":          "சேமி",
  "common.cancel":        "ரத்து",
  "common.confirm":       "உறுதிப்படுத்து",
  "common.back":          "பின்",
  "common.edit":          "திருத்து",
  "common.undo":          "மீட்டமை",
  "common.next":          "அடுத்து",
  "common.done":          "முடிந்தது",
  "common.close":         "மூடு",
  "common.retry":         "மீண்டும்",
  "common.loading":       "ஏற்றுகிறது…",
  "common.language":      "மொழி",
  "nav.dashboard":        "டாஷ்போர்டு",
  "nav.matches":          "போட்டிகள்",
  "nav.players":          "வீரர்கள்",
  "nav.teams":            "அணிகள்",
  "nav.competitions":     "தொடர்கள்",
  "nav.organisation":     "நிறுவனம்",
  "nav.subscription":     "சந்தா",
  "nav.help":             "உதவி",
  "nav.signout":          "வெளியேறு",
  "bbb.save_ball":        "பந்து சேமி",
  "bbb.undo_ball":        "பந்து மீள்",
  "bbb.edit_ball":        "பந்து திருத்து",
  "bbb.scorecard":        "மதிப்பெண் அட்டை",
  "extras.wide":          "வைடு",
  "extras.no_ball":       "நோ பால்",
  "extras.bye":           "பை",
  "extras.leg_bye":       "லெக் பை",
  "extras.free_hit":      "ஃப்ரீ ஹிட்",
  "extras.wicket":        "விக்கெட்",
  "wicket.bowled":        "பவுல்டு",
  "wicket.caught":        "கேட்ச் ஔட்",
  "wicket.run_out":       "ரன் ஔட்",
  "wicket.stumped":       "ஸ்டம்ப்டு",
  "wicket.retired":       "ஓய்வு",
  "spatial.pitch_map":    "பிட்ச் வரைபடம்",
  "spatial.height_impact":"உயரம்",
  "spatial.wagon_wheel":  "வாகன் சக்கரம்",
  "header.synced":        "சேமிக்கப்பட்டது",
  "header.offline":       "இணையம் இல்லை",
  "header.live_link":     "லைவ் இணைப்பு",
  "header.copied":        "நகலெடுக்கப்பட்டது",
  "save.failed":          "சேமிப்பு தோல்வி",
  "save.just_now":        "இப்போதுதான்",
  "chat.title":           "ஸ்கோரிங் உதவியாளர்",
  "chat.placeholder":     "ஏதாவது கேளுங்கள்…",
  "chat.thinking":        "சிந்திக்கிறது…",
};

const te = {
  ...en,
  "common.save":          "సేవ్ చేయి",
  "common.cancel":        "రద్దు",
  "common.confirm":       "ధృవీకరించు",
  "common.back":          "వెనుక",
  "common.edit":          "సవరించు",
  "common.undo":          "రద్దు",
  "common.next":          "తరువాత",
  "common.done":          "పూర్తయింది",
  "common.close":         "మూసివేయి",
  "common.language":      "భాష",
  "nav.dashboard":        "డాష్‌బోర్డ్",
  "nav.matches":          "మ్యాచులు",
  "nav.players":          "ఆటగాళ్ళు",
  "nav.teams":            "జట్లు",
  "bbb.save_ball":        "బంతి సేవ్",
  "bbb.undo_ball":        "బంతి రద్దు",
  "bbb.edit_ball":        "బంతి సవరించు",
  "bbb.scorecard":        "స్కోర్‌కార్డ్",
  "extras.wide":          "వైడ్",
  "extras.no_ball":       "నో బాల్",
  "extras.wicket":        "వికెట్",
  "wicket.bowled":        "బౌల్డ్",
  "wicket.caught":        "క్యాచ్ ఔట్",
  "wicket.run_out":       "రన్ ఔట్",
  "wicket.stumped":       "స్టంప్డ్",
  "header.synced":        "సేవ్ చేయబడింది",
  "header.offline":       "ఆఫ్‌లైన్",
  "save.failed":          "సేవ్ విఫలమైంది",
  "chat.title":           "స్కోరింగ్ సహాయకుడు",
  "chat.placeholder":     "ఏదైనా అడగండి…",
};

const kn = {
  ...en,
  "common.save":          "ಉಳಿಸಿ",
  "common.cancel":        "ರದ್ದು",
  "common.confirm":       "ದೃಢೀಕರಿಸಿ",
  "common.back":          "ಹಿಂದೆ",
  "common.edit":          "ಬದಲಾಯಿಸಿ",
  "common.undo":          "ರದ್ದುಮಾಡಿ",
  "common.next":          "ಮುಂದೆ",
  "common.done":          "ಮುಗಿದಿದೆ",
  "common.close":         "ಮುಚ್ಚಿ",
  "common.language":      "ಭಾಷೆ",
  "nav.dashboard":        "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
  "nav.matches":          "ಪಂದ್ಯಗಳು",
  "nav.players":          "ಆಟಗಾರರು",
  "nav.teams":            "ತಂಡಗಳು",
  "bbb.save_ball":        "ಬಾಲ್ ಉಳಿಸಿ",
  "bbb.undo_ball":        "ಬಾಲ್ ರದ್ದು",
  "bbb.edit_ball":        "ಬಾಲ್ ಬದಲಾಯಿಸಿ",
  "bbb.scorecard":        "ಸ್ಕೋರ್‌ಕಾರ್ಡ್",
  "extras.wide":          "ವೈಡ್",
  "extras.no_ball":       "ನೋ ಬಾಲ್",
  "extras.wicket":        "ವಿಕೆಟ್",
  "wicket.bowled":        "ಬೌಲ್ಡ್",
  "wicket.caught":        "ಕ್ಯಾಚ್ ಔಟ್",
  "wicket.run_out":       "ರನ್ ಔಟ್",
  "wicket.stumped":       "ಸ್ಟಂಪ್ಡ್",
  "header.synced":        "ಉಳಿಸಲಾಗಿದೆ",
  "header.offline":       "ಆಫ್‌ಲೈನ್",
  "save.failed":          "ಉಳಿಸಲಾಗಲಿಲ್ಲ",
  "chat.title":           "ಸ್ಕೋರಿಂಗ್ ಸಹಾಯಕ",
  "chat.placeholder":     "ಏನಾದರೂ ಕೇಳಿ…",
};

const mr = {
  ...en,
  "common.save":          "जतन करा",
  "common.cancel":        "रद्द",
  "common.confirm":       "पुष्टी",
  "common.back":          "मागे",
  "common.edit":          "बदला",
  "common.undo":          "मागे घ्या",
  "common.next":          "पुढे",
  "common.done":          "झाले",
  "common.close":         "बंद",
  "common.language":      "भाषा",
  "nav.dashboard":        "डॅशबोर्ड",
  "nav.matches":          "सामने",
  "nav.players":          "खेळाडू",
  "nav.teams":            "संघ",
  "bbb.save_ball":        "चेंडू जतन",
  "bbb.undo_ball":        "चेंडू मागे",
  "bbb.edit_ball":        "चेंडू बदला",
  "bbb.scorecard":        "स्कोरकार्ड",
  "extras.wide":          "वाइड",
  "extras.no_ball":       "नो बॉल",
  "extras.wicket":        "विकेट",
  "wicket.bowled":        "बोल्ड",
  "wicket.caught":        "झेल",
  "wicket.run_out":       "धावबाद",
  "wicket.stumped":       "यष्टीचीत",
  "header.synced":        "जतन झाले",
  "header.offline":       "ऑफलाइन",
  "save.failed":          "जतन अयशस्वी",
  "chat.title":           "स्कोरिंग सहाय्यक",
  "chat.placeholder":     "काहीही विचारा…",
};

const bn = {
  ...en,
  "common.save":          "সংরক্ষণ",
  "common.cancel":        "বাতিল",
  "common.confirm":       "নিশ্চিত",
  "common.back":          "ফিরে যান",
  "common.edit":          "সম্পাদনা",
  "common.undo":          "পূর্বাবস্থা",
  "common.next":          "পরবর্তী",
  "common.done":          "সম্পন্ন",
  "common.close":         "বন্ধ",
  "common.language":      "ভাষা",
  "nav.dashboard":        "ড্যাশবোর্ড",
  "nav.matches":          "ম্যাচ",
  "nav.players":          "খেলোয়াড়",
  "nav.teams":            "দল",
  "bbb.save_ball":        "বল সংরক্ষণ",
  "bbb.undo_ball":        "বল ফেরাও",
  "bbb.edit_ball":        "বল সম্পাদনা",
  "bbb.scorecard":        "স্কোরকার্ড",
  "extras.wide":          "ওয়াইড",
  "extras.no_ball":       "নো বল",
  "extras.wicket":        "উইকেট",
  "wicket.bowled":        "বোল্ড",
  "wicket.caught":        "ক্যাচ আউট",
  "wicket.run_out":       "রান আউট",
  "wicket.stumped":       "স্টাম্পড",
  "header.synced":        "সংরক্ষিত",
  "header.offline":       "অফলাইন",
  "save.failed":          "সংরক্ষণ ব্যর্থ",
  "chat.title":           "স্কোরিং সহায়ক",
  "chat.placeholder":     "যেকোনো প্রশ্ন করুন…",
};

const gu = {
  ...en,
  "common.save":          "સાચવો",
  "common.cancel":        "રદ કરો",
  "common.confirm":       "પુષ્ટિ",
  "common.back":          "પાછા",
  "common.edit":          "બદલો",
  "common.undo":          "પૂર્વવત્",
  "common.next":          "આગળ",
  "common.done":          "થયું",
  "common.close":         "બંધ",
  "common.language":      "ભાષા",
  "nav.dashboard":        "ડેશબોર્ડ",
  "nav.matches":          "મેચ",
  "nav.players":          "ખેલાડીઓ",
  "nav.teams":            "ટીમો",
  "bbb.save_ball":        "બૉલ સાચવો",
  "bbb.undo_ball":        "બૉલ પાછું",
  "bbb.edit_ball":        "બૉલ બદલો",
  "bbb.scorecard":        "સ્કોરકાર્ડ",
  "extras.wide":          "વાઇડ",
  "extras.no_ball":       "નો બૉલ",
  "extras.wicket":        "વિકેટ",
  "wicket.bowled":        "બૉલ્ડ",
  "wicket.caught":        "કેચ આઉટ",
  "wicket.run_out":       "રન આઉટ",
  "wicket.stumped":       "સ્ટમ્પ્ડ",
  "header.synced":        "સચવાયું",
  "header.offline":       "ઑફલાઇન",
  "save.failed":          "સાચવવામાં નિષ્ફળ",
  "chat.title":           "સ્કોરિંગ સહાયક",
  "chat.placeholder":     "કોઈપણ પ્રશ્ન પૂછો…",
};

export const TRANSLATIONS = { en, hi, ta, te, kn, mr, bn, gu };
